import React, { useState, useEffect } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Chip
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error,
  HourglassEmpty
} from '@mui/icons-material';
import { api } from '../../utils/api';
import { useWorkspace } from '../../context/WorkspaceContext';

const SyncSubtasksButton = ({ sessionId, onSyncComplete }) => {
  const { currentOrganization } = useWorkspace();
  const organizationId = currentOrganization?.id || null;
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingSubtasks, setPendingSubtasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [error, setError] = useState(null);

  // Fetch pending subtasks count
  useEffect(() => {
    const fetchPending = async () => {
      if (!sessionId) return;

      setLoading(true);
      try {
        const response = await api.get(`/subtasks/session/${sessionId}/pending`);
        setPendingCount(response.data.count);
        setPendingSubtasks(response.data.subtasks);
      } catch (err) {
        console.error('Error fetching pending subtasks:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPending();

    // Refresh every 30 seconds
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [sessionId]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setSyncResults(null);
    setError(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    if (syncResults) {
      // Refresh pending count after sync
      const fetchPending = async () => {
        try {
          const response = await api.get(`/subtasks/session/${sessionId}/pending`);
          setPendingCount(response.data.count);
          setPendingSubtasks(response.data.subtasks);
        } catch (err) {
          console.error('Error fetching pending subtasks:', err);
        }
      };
      fetchPending();
      if (onSyncComplete) onSyncComplete();
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    setSyncResults(null);

    try {
      const subtaskIds = pendingSubtasks.map(s => s.id);
      const response = await api.post('/jira/subtasks/sync', {
        subtaskIds,
        organizationId: organizationId || undefined,
      });
      setSyncResults(response.data);
    } catch (err) {
      console.error('Error syncing subtasks:', err);
      setError(err.response?.data?.message || err.message || 'Failed to sync subtasks');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return null;
  }

  if (pendingCount === 0) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        color="secondary"
        startIcon={<CloudUpload />}
        onClick={handleOpenDialog}
        size="small"
        sx={{ ml: 1 }}
      >
        Sync Subtasks ({pendingCount})
      </Button>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Sync Subtasks to Jira
        </DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {!syncResults ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {pendingCount} subtask{pendingCount !== 1 ? 's' : ''} will be created in Jira:
              </Typography>

              <List dense>
                {pendingSubtasks.map((subtask) => (
                  <ListItem key={subtask.id}>
                    <ListItemIcon>
                      <HourglassEmpty color="action" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={subtask.title}
                      secondary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" color="text.secondary">
                            Parent: {subtask.parentItem?.jiraKey || subtask.parentItem?.externalId}
                          </Typography>
                          {subtask.estimate && (
                            <Chip
                              label={subtask.estimateType === 'hours' ? `${subtask.estimate}h` : `${subtask.estimate} pts`}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <>
              <Alert
                severity={syncResults.results?.some(r => r.status === 'failed') ? 'warning' : 'success'}
                sx={{ mb: 2 }}
              >
                {syncResults.message}
              </Alert>

              <List dense>
                {syncResults.results?.map((result) => {
                  const subtask = pendingSubtasks.find(s => s.id === result.subtaskId);
                  return (
                    <ListItem key={result.subtaskId}>
                      <ListItemIcon>
                        {result.status === 'synced' ? (
                          <CheckCircle color="success" fontSize="small" />
                        ) : (
                          <Error color="error" fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={subtask?.title || `Subtask ${result.subtaskId}`}
                        secondary={
                          result.status === 'synced' ? (
                            <Typography
                              variant="caption"
                              component="a"
                              href={result.jiraUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                            >
                              {result.jiraKey}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="error">
                              {result.error}
                            </Typography>
                          )
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {syncResults ? 'Close' : 'Cancel'}
          </Button>
          {!syncResults && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSync}
              disabled={syncing || pendingCount === 0}
              startIcon={syncing ? <CircularProgress size={20} /> : <CloudUpload />}
            >
              {syncing ? 'Syncing...' : 'Sync to Jira'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SyncSubtasksButton;
